import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAllVideoPodcasts, getUserVideoPodcasts, deleteVideoPodcast } from '../../api/videoPodcast';
import { useAuth } from '../../components/AuthContext';
import VideoPodcastCard from '../../components/VideoPodcastCard';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import toast from 'react-hot-toast';

const VideoPodcastChannelPage = () => {
  const { currentUser, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('discover');
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'General', label: 'General' },
    { id: 'Technology', label: 'Technology' },
    { id: 'Entertainment', label: 'Entertainment' },
    { id: 'Sports', label: 'Sports' },
    { id: 'Business', label: 'Business' },
    { id: 'Health', label: 'Health' },
    { id: 'Education', label: 'Education' },
    { id: 'News', label: 'News' },
    { id: 'Lifestyle', label: 'Lifestyle' },
    { id: 'Music', label: 'Music' },
    { id: 'Comedy', label: 'Comedy' },
    { id: 'Other', label: 'Other' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, selectedCategory, debouncedSearch]);

  const fetchPodcasts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const category = selectedCategory === 'all' ? null : selectedCategory;

      let data;
      if (activeTab === 'mine') {
        if (!currentUser?.id) {
          setPodcasts([]);
          setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
          return;
        }
        data = await getUserVideoPodcasts(
          currentUser.id,
          page,
          20,
          false,
          debouncedSearch || null
        );
      } else {
        data = await getAllVideoPodcasts(
          page,
          20,
          category,
          null,
          debouncedSearch || null
        );
      }

      setPodcasts(data?.podcasts || []);
      setPagination(data?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
      });
    } catch (err) {
      console.error('Error fetching video podcasts:', err);
      setError(err.message || 'Failed to load video podcasts');
      toast.error(err.message || 'Failed to load video podcasts');
      setPodcasts([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentUser?.id, debouncedSearch, page, selectedCategory]);

  useEffect(() => {
    fetchPodcasts();
  }, [fetchPodcasts]);

  const handleEdit = (podcast) => {
    navigate(`/video-podcasts/${podcast._id}/edit`);
  };

  const handleDelete = async (podcast) => {
    if (!token) {
      toast.error('Please login to delete podcasts');
      navigate('/login');
      return;
    }
    if (!window.confirm(`Delete "${podcast.title}"? This cannot be undone.`)) return;

    try {
      await deleteVideoPodcast(podcast._id, token);
      toast.success('Podcast deleted');
      fetchPodcasts();
    } catch (err) {
      toast.error(err.message || 'Failed to delete podcast');
    }
  };

  if (loading && podcasts.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="app-page min-h-screen py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/freelancer-news')}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
            Back
          </button>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white md:text-2xl">
            Video Podcasts
          </h1>
          {currentUser ? (
            <Link
              to="/video-podcasts/create"
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#2563eb] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#1d4ed8]"
            >
              <AddIcon sx={{ fontSize: 18 }} />
              Create
            </Link>
          ) : (
            <div className="w-[88px]" aria-hidden="true" />
          )}
        </div>

        <div className="mb-8">
          <p className="text-gray-600 dark:text-gray-400">
            Discover community podcasts or manage your own uploads
          </p>
        </div>

        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => setActiveTab('discover')}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                activeTab === 'discover'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Public Feed
            </button>
            <button
              type="button"
              onClick={() => {
                if (!currentUser) {
                  toast.error('Please login to view your podcasts');
                  navigate('/login');
                  return;
                }
                setActiveTab('mine');
              }}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                activeTab === 'mine'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              My Podcasts
            </button>
          </div>

          <div className="relative w-full lg:max-w-md">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search podcasts..."
              className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>
        </div>

        {activeTab === 'discover' && (
          <div className="mb-6 flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => {
                  setSelectedCategory(category.id);
                  setPage(1);
                }}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/50">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {podcasts.length === 0 && !loading ? (
          <div className="py-12 text-center">
            <VideoLibraryIcon className="mx-auto mb-4 h-16 w-16 text-gray-400" />
            <p className="text-lg text-gray-500 dark:text-gray-400">
              {activeTab === 'mine' ? 'You have not uploaded any podcasts yet' : 'No video podcasts found'}
            </p>
            {activeTab === 'mine' && currentUser && (
              <Link
                to="/video-podcasts/create"
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
              >
                <AddIcon sx={{ fontSize: 18 }} />
                Create your first podcast
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {podcasts.map((podcast) => (
                <VideoPodcastCard
                  key={podcast._id}
                  podcast={podcast}
                  showOwnerActions={activeTab === 'mine'}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {pagination.pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  className="rounded-lg bg-white px-4 py-2 text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                <span className="text-gray-700 dark:text-gray-300">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(pagination.pages, prev + 1))}
                  disabled={page === pagination.pages}
                  className="rounded-lg bg-white px-4 py-2 text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VideoPodcastChannelPage;
